/**
 * Recovery Commons Help & Tour Content
 * 
 * This file contains all help topics for the guided tour and reference system.
 * Images are stored in public/help-images/
 */

export interface HelpTopic {
  id: string;
  number: number;
  title: string;
  category: string;
  route?: string;
  description: string;
  details?: string;
  steps?: string[];
  tips?: string[];
  relatedTopics?: string[];
  images: {
    desktop?: string;
    mobile?: string;
  };
  searchKeywords?: string[];
}

export interface HelpCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  topicIds: string[];
}

export const helpTopics: HelpTopic[] = [
  // ========================================
  // GETTING STARTED (Topics 1-7)
  // ========================================
  {
    id: 'landing-page',
    number: 1,
    title: 'Welcome to Recovery Commons',
    category: 'getting-started',
    route: '/',
    description: 'Your starting point for exploring Recovery Commons and beginning your recovery journey.',
    details: 'The homepage offers three paths: assess whether you might have an addiction, discover recovery programs and resources, or track your personal recovery journey privately and securely.',
    steps: [
      'Choose "Assess" if you want to take a self-assessment to understand if you might have an addiction',
      'Choose "Discover" if you want to learn about different recovery programs and resources',
      'Choose "My Journey" if you\'re ready to create an account and start tracking your recovery work'
    ],
    tips: [
      'You don\'t need an account to take the self-assessment or browse recovery information',
      'All your recovery journey data is private and encrypted - only you can see it',
    ],
    relatedTopics: ['create-account', 'self-assessment', 'discovery'],
    images: {
      desktop: '1-landing page.png',
      mobile: '2-landing page mobile.png'
    },
    searchKeywords: ['homepage', 'start', 'welcome', 'begin', 'first time']
  },

  {
    id: 'create-account',
    number: 3,
    title: 'Create Your Account',
    category: 'getting-started',
    route: '/register',
    description: 'Sign up for Recovery Commons to begin your private, secure recovery journey.',
    details: 'Creating an account allows you to privately track your progress through recovery programs, complete step work, manage inventories, and securely store all your recovery data. Your email serves as your username, and all your data is encrypted.',
    steps: [
      'Click "Create Account" from the homepage',
      'Enter your first name (used for personalization)',
      'Enter your email address (this becomes your username)',
      'Create a strong password (minimum 8 characters)',
      'Confirm your password by entering it again',
      'Click the "Create Account" button',
      'Check your email for a verification code',
      'Enter the code to verify your account'
    ],
    tips: [
      'Use an email you check regularly - you\'ll receive verification codes and optional notifications',
      'Choose a strong, unique password to protect your privacy',
      'Your first name is only used within the app and is never shared',
      'Click the eye icon to show/hide your password while typing',
      'Already have an account? Click "Login" at the bottom'
    ],
    relatedTopics: ['verify-account', 'select-program', 'dashboard'],
    images: {
      desktop: '3-create account.png',
      mobile: '3-create account mobile.png'
    },
    searchKeywords: ['signup', 'register', 'new account', 'join', 'email', 'password']
  },

  {
    id: 'verify-account',
    number: 4,
    title: 'Verify Your Email',
    category: 'getting-started',
    description: 'Confirm your email address by entering the verification code sent to your inbox.',
    details: 'Email verification ensures that you have access to the email address you provided and helps protect your account security. You\'ll receive a 6-digit code that\'s valid for 15 minutes.',
    steps: [
      'After creating your account, check your email inbox',
      'Look for an email from Recovery Commons (no-reply@recoverycommons.com)',
      'Copy the 6-digit verification code from the email',
      'Enter the code in the verification screen',
      'Click "Verify" to complete the process'
    ],
    tips: [
      'Verification codes expire after 15 minutes for security',
      'Check your spam/junk folder if you don\'t see the email',
      'Click "Resend Code" if your code expired or you didn\'t receive it',
      'The email includes a sample showing what the code looks like'
    ],
    relatedTopics: ['create-account', 'select-program'],
    images: {
      desktop: '4-verify account.png',
      mobile: '4-verify account mobile.png',
    },
    searchKeywords: ['verify', 'verification code', 'email confirmation', 'code', 'activate']
  },

  {
    id: 'verify-email-sample',
    number: 5,
    title: 'Verification Email',
    category: 'getting-started',
    description: 'Example of the verification email you\'ll receive.',
    details: 'This shows what the verification email looks like so you know what to look for in your inbox. The email comes from no-reply@recoverycommons.com and contains your 6-digit verification code.',
    relatedTopics: ['verify-account', 'create-account'],
    images: {
      desktop: '5-verify account email.png'
    },
    searchKeywords: ['email', 'verification email', 'sample email', 'what email looks like']
  },

  {
    id: 'select-program',
    number: 6,
    title: 'Select Your Primary Recovery Program',
    category: 'getting-started',
    route: '/onboarding',
    description: 'Choose which recovery program you\'ll focus on in Recovery Commons.',
    details: 'Your primary program determines which steps, tools, and resources you\'ll see first. You can add additional programs later and switch between them at any time from your dashboard or profile.',
    steps: [
      'Review the list of available recovery programs',
      'Click on the program that best matches your recovery focus',
      'Click "Continue" to proceed to your dashboard',
      'You can change or add programs anytime from your Profile'
    ],
    tips: [
      'You can work multiple programs - just set one as your primary focus',
      'Your primary program appears first on your dashboard',
      'All program work is kept completely separate and private',
      'You can switch your primary program anytime in your Profile settings'
    ],
    relatedTopics: ['dashboard', 'profile'],
    images: {
      desktop: '6-select your primary recovery program.png',
      mobile: '6-select your primary recovery program mobile.png'
    },
    searchKeywords: ['program', 'saa', 'aa', 'na', 'primary program', 'choose program', 'select']
  },

  // ========================================
  // DASHBOARD & NAVIGATION (Topics 8-9)
  // ========================================
  {
    id: 'dashboard',
    number: 8,
    title: 'Your Personal Dashboard',
    category: 'dashboard',
    route: '/dashboard',
    description: 'Your home base for accessing your recovery work, tracking progress, and exploring resources.',
    details: 'The Personal Dashboard is where you land after logging in. It shows your primary recovery program with quick access to your step progress, and provides entry points to self-assessment tools and discovery resources.',
    steps: [
      'View your primary program name and icon at the top',
      'Click "My [Program] 12 Step Progress" to see your step-by-step work',
      'Click "Start Step 1" (or current step) to begin or continue step work',
      'Click "More [Program] Resources" to access tools, readings, and videos',
      'Click "+ Add Another Program" to work multiple programs',
      'Scroll down to access "Assess" and "Discover" features'
    ],
    tips: [
      'The dashboard always shows your most recent progress',
      'Your progress is automatically saved as you work',
      'Use the hamburger menu (☰) in the top right to access Profile, Share, Feedback, and more',
      'The breadcrumb trail at the top shows where you are and helps you navigate back'
    ],
    relatedTopics: ['step-status', 'hamburger-menu', 'profile'],
    images: {
      desktop: '8-personal dashboard.png',
      mobile: '8-personal dashboard mobile.png'
    },
    searchKeywords: ['dashboard', 'home', 'main page', 'after login', 'my dashboard']
  },

  {
    id: 'step-status',
    number: 9,
    title: 'SAA 12-Step Status Overview',
    category: 'dashboard',
    route: '/saa/12-steps',
    description: 'View your progress across all 12 steps at a glance.',
    details: 'The 12-Step Status page shows which steps you\'ve completed, which you\'re working on, and which are still to do. Each step card displays your completion status and provides quick access to continue your work.',
    steps: [
      'Green checkmark = Step fully completed',
      'Yellow "In Progress" = Step has some work done',
      'Gray "To Do" = Step not yet started',
      'Click any step card to open that step and continue your work',
      'Click "Return to Dashboard" to go back'
    ],
    tips: [
      'Steps must generally be completed in order',
      'Completing all actions in a step marks it as complete',
      'Your progress is automatically tracked as you work',
      'Use this page to see the big picture of your recovery journey'
    ],
    relatedTopics: ['dashboard', 'step-one-overview'],
    images: {
      desktop: '9-saa 12 step status.png',
      mobile: '9-saa 12 step status mobile.png'
    },
    searchKeywords: ['12 steps', 'progress', 'status', 'overview', 'steps list']
  },

  // ========================================
  // WORKING THE STEPS - Step 1 Example (Topics 9.5-9.8)
  // ========================================
  {
    id: 'step-one-overview',
    number: 9.5,
    title: 'Working a Step (Step One Example)',
    category: 'steps',
    route: '/saa/12-steps/step-1',
    description: 'How to work through a step page - using Step One as an example that applies to all steps.',
    details: 'Each step page in Recovery Commons follows the same structure: a list of action items to complete, access to recovery literature (Green Book), related videos, and progress tracking. Step One is shown here, but this same pattern applies to all 12 steps.',
    steps: [
      'Read the step description at the top to understand what this step is about',
      'Review the list of actions - these are tasks to complete the step',
      'Check off actions as you complete them (your progress saves automatically)',
      'Click on expandable actions to see more details and sub-tasks',
      'Use the Green Book button to read relevant recovery literature',
      'Watch related videos to deepen your understanding',
      'Complete all actions to mark the step as finished'
    ],
    tips: [
      'You don\'t have to complete a step in one session - your progress is saved',
      'Actions collapse automatically when completed to reduce clutter',
      'Some actions require answering questions or completing inventories',
      'Green checkmarks indicate completed actions',
      'All step pages work the same way - once you learn Step One, you know them all'
    ],
    relatedTopics: ['green-book-reading', 'step-videos', 'step-completed'],
    images: {
      desktop: '9.5-step 1.png'
    },
    searchKeywords: ['step', 'actions', 'working steps', 'how to', 'complete step', 'step one']
  },

  {
    id: 'green-book-reading',
    number: 9.6,
    title: 'Reading Recovery Literature',
    category: 'steps',
    description: 'Access and read recovery program literature like the Green Book directly in the app.',
    details: 'Many steps include reading from recovery literature. Recovery Commons provides the full text of these readings so you can read, reflect, and return to them anytime. The content is formatted for easy reading on any device.',
    steps: [
      'Click the reading button (e.g., "Read Green Book") on any step page',
      'The reading opens in a clean, easy-to-read format',
      'Scroll through the content at your own pace',
      'Click "Close" or "Back to Step" when finished',
      'The reading remains available - you can return to it anytime'
    ],
    tips: [
      'Take your time reading - there\'s no rush',
      'Readings are available on both desktop and mobile devices',
      'You can read the same content multiple times as you progress',
      'Some readings are referenced across multiple steps'
    ],
    relatedTopics: ['step-one-overview', 'step-videos'],
    images: {
      desktop: '9.6-step 1 green book reading.png',
      mobile: '9.6-step 1 green book reading mobile.png'
    },
    searchKeywords: ['green book', 'reading', 'literature', 'text', 'book', 'SAA book']
  },

  {
    id: 'step-videos',
    number: 9.7,
    title: 'Watching Step Videos',
    category: 'steps',
    description: 'Watch helpful videos related to each step to deepen your understanding.',
    details: 'Some steps include videos that explain concepts, share experiences, or provide guidance. Videos open in a viewer that works on all devices.',
    steps: [
      'Click the video button on any step page',
      'The video opens in a viewer',
      'Use standard video controls (play, pause, volume)',
      'Click "Close" when finished to return to the step page'
    ],
    tips: [
      'Videos are optional but can provide valuable insights',
      'You can watch videos multiple times',
      'Videos work on both desktop and mobile devices'
    ],
    relatedTopics: ['step-one-overview', 'green-book-reading'],
    images: {
      desktop: '9.7-step 1 video.png',
      mobile: '9.7-step 1 video mobile.png'
    },
    searchKeywords: ['video', 'watch', 'media', 'play']
  },

  {
    id: 'step-completed',
    number: 9.8,
    title: 'Completing a Step',
    category: 'steps',
    description: 'What happens when you finish all actions in a step.',
    details: 'When you complete the last action in a step, the entire step is marked as complete. You\'ll see a green "Completed" status, and the system will guide you to the next step in your recovery journey.',
    steps: [
      'Complete all required actions in the step',
      'The step automatically changes to "Completed" status with a green checkmark',
      'Completed actions collapse to show you\'ve finished them',
      'The next step becomes available for you to start',
      'Your progress is reflected in your 12-Step Status overview'
    ],
    tips: [
      'You can always return to completed steps to review your work',
      'Completing actions out of order is okay - the step completes when all are done',
      'Some steps require inventories or question answers before completion',
      'Your completion dates are tracked for your personal history'
    ],
    relatedTopics: ['step-one-overview', 'step-status'],
    images: {
      desktop: '9.8-step 1 all steps completed.png',
      mobile: '9.8-step 1 all steps completed mobile.png'
    },
    searchKeywords: ['complete', 'finished', 'done', 'completed step', 'checkmark']
  },

  // ========================================
  // STEP FOUR INVENTORIES (Topics 10-14)
  // ========================================
  {
    id: 'step-four-actions',
    number: 10,
    title: 'Step Four - Getting Started',
    category: 'inventories',
    route: '/saa/12-steps/step-4',
    description: 'Introduction to Step Four and its action items.',
    details: 'Step Four is about making a searching and fearless moral inventory. This step has preparatory actions to complete before you begin writing your three inventories: Resentments, Fears, and Harms Done.',
    steps: [
      'Read the Step Four introduction to understand what a moral inventory is',
      'Complete the preparatory actions (read Green Book, etc.)',
      'When ready, click "Resentments Inventory" to begin your first inventory',
      'Complete all three inventories at your own pace',
      'Check off each action as you complete it'
    ],
    tips: [
      'Take your time with inventories - they\'re deep, personal work',
      'Your inventories are completely private and encrypted',
      'You can work on inventories over multiple sessions',
      'All your entries are automatically saved'
    ],
    relatedTopics: ['resentments-inventory', 'inventory-detail'],
    images: {
      mobile: '10-step 4 actions mobile.png'
    },
    searchKeywords: ['step four', 'step 4', 'inventory', 'moral inventory', 'resentments', 'fears', 'harms']
  },

  {
    id: 'resentments-inventory',
    number: 11,
    title: 'Working Your Inventories',
    category: 'inventories',
    route: '/saa/12-steps/step-4',
    description: 'How to add and manage inventory entries - applies to Resentments, Fears, and Harms Done.',
    details: 'Step Four includes three inventories: Resentments (people/institutions/principles you resent), Fears (things you\'re afraid of), and Harms Done (people you\'ve harmed). All three work the same way - this guide uses Resentments as the example.',
    steps: [
      'Click the inventory tab (Resentments, Fears, or Harms Done)',
      'Type a name in the "Who do I resent?" field (or appropriate field for the inventory type)',
      'Press Enter or click Add to create the entry',
      'The entry appears in your list and automatically expands for you to fill in details',
      'Complete all the fields (Why, Feeling, Impacts, My Part, etc.)',
      'Fields save automatically as you type - look for the checkmark',
      'Click outside the entry to collapse it',
      'Repeat to add more entries'
    ],
    tips: [
      'Your entries save automatically every 10 seconds while editing',
      'Data also saves when you close the entry or switch tabs',
      'Press Enter to quickly add a new entry and start filling it in',
      'Use the checkboxes to select which life areas were impacted',
      'The same process works for all three inventories - only the field labels change',
      'You can come back and edit entries anytime'
    ],
    relatedTopics: ['inventory-detail', 'step-four-actions'],
    images: {
      desktop: '12-step 4 resentment inventory modal .png',
      mobile: '11-step 4 resentment inventories mobile.png'
    },
    searchKeywords: ['inventory', 'resentments', 'fears', 'harms', 'add entry', 'step 4', 'step four']
  },

  {
    id: 'inventory-detail',
    number: 14,
    title: 'Filling in Inventory Details',
    category: 'inventories',
    description: 'How to complete all fields in an inventory entry for thorough self-examination.',
    details: 'Each inventory entry has multiple fields to help you examine your part in the situation. Taking time with each field leads to deeper self-understanding and more effective recovery work.',
    steps: [
      'After adding an entry, it automatically expands to show all fields',
      'Fill in "Why I resent them" - what they did that hurt you',
      'Describe your feelings - how did this affect you emotionally',
      'Check which life areas were impacted (self-esteem, security, relationships, etc.)',
      'Reflect on "My part" - your role in the situation (this is the hardest but most important)',
      'Fields save automatically as you type',
      'Look for the checkmark (✓) next to the entry name when saved',
      'Click elsewhere or press the collapse button to close the entry'
    ],
    tips: [
      'Be honest - this is for you, not anyone else',
      'The "My Part" field is where the real growth happens',
      'It\'s okay to leave fields incomplete and come back later',
      'Auto-save means you never lose your work',
      'For Fears inventory: fields are "Fear" and "Why"',
      'For Harms Done: fields are "Who I harmed", "How I harmed them", and checkboxes for types of harm',
      'You can edit any entry anytime by clicking on it'
    ],
    relatedTopics: ['resentments-inventory', 'field-help'],
    images: {
      desktop: '14-step 4 resentment detail .png'
    },
    searchKeywords: ['inventory details', 'my part', 'feelings', 'impacts', 'complete entry', 'fill in']
  },

  // ========================================
  // UI FEATURES (Topics 15-22)
  // ========================================
  {
    id: 'field-help',
    number: 15,
    title: 'Getting Help on Any Field',
    category: 'features',
    description: 'Quick help is available throughout the app via help buttons.',
    details: 'Many fields and features have a blue help button (circle with ?) next to them. Click these to get specific guidance about that field or feature without leaving the page.',
    steps: [
      'Look for blue circle buttons with a "?" symbol',
      'Click the help button to see guidance',
      'Read the help information',
      'Click "OK" or close to return to what you were doing'
    ],
    tips: [
      'Help buttons don\'t interrupt your work - they\'re just there when you need them',
      'You can tab through fields without triggering help buttons',
      'Help text is specific to the field or feature you\'re asking about'
    ],
    relatedTopics: ['inventory-detail', 'profile'],
    images: {
      desktop: '15-field help.png'
    },
    searchKeywords: ['help button', 'question mark', 'field help', 'what does this mean', '?']
  },

  {
    id: 'hamburger-menu',
    number: 16,
    title: 'Navigation Menu',
    category: 'features',
    description: 'Access all major features from the menu in the top-right corner.',
    details: 'The hamburger menu (☰) gives you quick access to your Profile, Share snapshots, provide Feedback, Backup your data, and Log out. It\'s always available from any page.',
    steps: [
      'Click the ☰ icon in the top-right corner',
      'Choose from the menu options:',
      '  • Profile - Update your settings and manage sponsors',
      '  • Share - Create and send progress snapshots',
      '  • Feedback - Send suggestions or report issues',
      '  • Backup & Restore - Save or load your data',
      '  • Help & Tour - Open this help system',
      '  • Log Out - Sign out of your account'
    ],
    tips: [
      'The menu is available from every page in the app',
      'Click outside the menu to close it without selecting anything',
      'Your current page is remembered when you return from menu actions'
    ],
    relatedTopics: ['profile', 'share', 'feedback', 'backup'],
    images: {
      desktop: '16-hamburger menu.png'
    },
    searchKeywords: ['menu', 'navigation', 'hamburger', 'logout', 'log out']
  },

  {
    id: 'profile',
    number: 17,
    title: 'Your Profile Settings',
    category: 'features',
    route: '/profile',
    description: 'Manage your account information, program preferences, and view your account details.',
    details: 'Your Profile shows your account information and allows you to update your name, view your membership details, and manage your recovery program preferences.',
    steps: [
      'Open the hamburger menu (☰) and select "Profile"',
      'View your email, name, member since date, and last login',
      'Click "Edit Profile" to update your first name or other settings',
      'Scroll down to see your Program Preferences',
      'Add or change your primary recovery program',
      'Save any changes you make'
    ],
    tips: [
      'Your email address cannot be changed (it\'s your username)',
      'Member since date shows when you created your account',
      'Last login helps you track your consistency',
      'Program preferences determine what you see on your dashboard'
    ],
    relatedTopics: ['sponsors', 'select-program'],
    images: {
      desktop: '17-profile modal.png'
    },
    searchKeywords: ['profile', 'account', 'settings', 'edit profile', 'my account', 'preferences']
  },

  {
    id: 'sponsors',
    number: 18,
    title: 'Managing Your Sponsors',
    category: 'features',
    description: 'Add and manage sponsors for your recovery programs.',
    details: 'Sponsors are experienced members who guide you through the steps. You can add a sponsor for each program you work, and they can receive progress snapshots when you choose to share your work.',
    steps: [
      'Open your Profile (from hamburger menu)',
      'Scroll to the "Sponsorship" section',
      'Click "+ Add Sponsor"',
      'Select which program the sponsor is for',
      'Enter your sponsor\'s name',
      'Enter your sponsor\'s email address',
      'Click "Add Sponsor" to save',
      'Repeat to add sponsors for other programs'
    ],
    tips: [
      'You can have one sponsor per program',
      'Sponsor emails are used when sharing progress snapshots',
      'Your sponsor\'s contact info is kept private and secure',
      'You can update or remove sponsors anytime',
      'Having a sponsor is optional but recommended in 12-step programs'
    ],
    relatedTopics: ['profile', 'share', 'send-snapshot'],
    images: {
      desktop: '18-profile modal sponsorship.png'
    },
    searchKeywords: ['sponsor', 'sponsorship', 'add sponsor', 'mentor', 'guide']
  },

  {
    id: 'share',
    number: 19,
    title: 'Sharing Your Progress',
    category: 'features',
    description: 'Create snapshots of your work to share with sponsors or for your records.',
    details: 'The Share feature lets you create secure, read-only snapshots of your 12-step progress or Step Four inventories. These snapshots can be shared with sponsors, sent via email, printed, or kept for your own records.',
    steps: [
      'Open the hamburger menu and select "Share"',
      'Choose which type of snapshot to create:',
      '  • SAA 12-Step Status - Shows your progress on all steps',
      '  • SAA Step Four Inventories - Shows your three inventories',
      'Click "Create Snapshot"',
      'Wait a moment while your snapshot is generated',
      'Your new snapshot appears at the top of the list',
      'Click a snapshot to view it',
      'Use the action buttons to share, print, or delete'
    ],
    tips: [
      'Snapshots expire after 7 days for privacy and security',
      'Create a new snapshot each time you want to share updated progress',
      'Old snapshots are automatically deleted after expiration',
      'Snapshots are read-only - they show work as it was when created',
      'The 12-Step Status snapshot only shows steps where you\'ve completed at least one action'
    ],
    relatedTopics: ['send-snapshot', 'sponsors'],
    images: {
      desktop: '19-share modal.png'
    },
    searchKeywords: ['share', 'snapshot', 'send to sponsor', 'progress report', 'export']
  },

  {
    id: 'send-snapshot',
    number: 20,
    title: 'Sending a Snapshot',
    category: 'features',
    description: 'Email your progress snapshot to your sponsor or another person.',
    details: 'After creating a snapshot, you can send it via email to your sponsor (if you\'ve added one in your Profile) or to any email address. The recipient gets a secure link that expires in 7 days.',
    steps: [
      'Create a snapshot first (if you haven\'t already)',
      'Click the share icon button (looks like connected dots) next to the snapshot',
      'Choose "Send to Sponsor" or "Send to Another Email"',
      'If sending to sponsor: Select which sponsor from the dropdown (if you have multiple)',
      'If sending to another email: Enter the recipient\'s email address',
      'The Send button enables when a valid selection is made',
      'Click "Send" to email the snapshot',
      'You\'ll see a confirmation when sent successfully'
    ],
    tips: [
      'Add sponsors in your Profile before using "Send to Sponsor"',
      'The email includes who sent it, what it is, and when the link expires',
      'Recipients don\'t need a Recovery Commons account to view snapshots',
      'The link expires in 7 days for your privacy and security',
      'You can send the same snapshot to multiple people if needed'
    ],
    relatedTopics: ['share', 'sponsors', 'profile'],
    images: {
      desktop: '20-send snapshot modal.png'
    },
    searchKeywords: ['send', 'email', 'share with sponsor', 'send to', 'email snapshot']
  },

  {
    id: 'feedback',
    number: 21,
    title: 'Send Feedback',
    category: 'features',
    description: 'Share suggestions, report issues, or ask questions about Recovery Commons.',
    details: 'Your feedback helps improve Recovery Commons for everyone. Use this feature to report bugs, suggest new features, ask questions, or share your experience.',
    steps: [
      'Open the hamburger menu and select "Feedback"',
      'Type your feedback, suggestion, or question in the text area',
      'Be as detailed as helpful - more context helps us understand',
      'Click "Submit Feedback"',
      'You\'ll see a confirmation that your feedback was received'
    ],
    tips: [
      'Feedback is sent directly to the Recovery Commons team',
      'Include specific details if reporting a problem (what page, what happened, etc.)',
      'Suggestions for new features are always welcome',
      'You can submit feedback anonymously or we\'ll use your email to follow up',
      'Check the "About" section for the current version number if reporting issues'
    ],
    relatedTopics: ['hamburger-menu'],
    images: {
      desktop: '21-feedback modal.png'
    },
    searchKeywords: ['feedback', 'suggestion', 'bug', 'problem', 'help', 'support', 'contact']
  },

  {
    id: 'backup',
    number: 22,
    title: 'Backup & Restore Your Data',
    category: 'features',
    description: 'Download a secure backup of your recovery data or restore from a previous backup.',
    details: 'Backup & Restore lets you download an encrypted copy of all your recovery data to your device. This protects against data loss and allows you to transfer your data if needed. Only you can decrypt and restore your backups.',
    steps: [
      'Open the hamburger menu and select "Backup & Restore"',
      'To backup: Click "Download Backup" and save the file to your device',
      'To restore: Click "Upload Backup", select your backup file, and confirm',
      'Backups are encrypted - keep your backup file safe',
      'Give your backup file a meaningful name (e.g., "recovery-backup-oct-2024.json")'
    ],
    tips: [
      'Create backups regularly, especially before major changes',
      'Backup files are encrypted - only you can restore them',
      'Restoring replaces your current data - be careful!',
      'Keep backup files in a secure location',
      'You can\'t restore someone else\'s backup - encryption prevents this'
    ],
    relatedTopics: ['profile', 'hamburger-menu'],
    images: {
      desktop: '22-backup and restore.png'
    },
    searchKeywords: ['backup', 'restore', 'download', 'save data', 'export', 'import']
  },

  // ========================================
  // SELF-ASSESSMENT (Topics 23-27)
  // ========================================
  {
    id: 'assessment-begin',
    number: 23,
    title: 'Self-Assessment Tool',
    category: 'assessment',
    route: '/self-assess',
    description: 'Take a confidential self-assessment to explore whether you might have an addiction.',
    details: 'The self-assessment is a private tool to help you reflect on your behaviors and patterns. It\'s based on standard addiction assessment frameworks and covers multiple areas. No account required - your answers stay on your device.',
    steps: [
      'Click "Take Self-Assessment" from the homepage or dashboard',
      'Read the introduction and privacy information',
      'Click "Begin Assessment" to start',
      'You\'ll see different addiction categories to explore',
      'Choose the areas you want to assess (you can select multiple)',
      'Answer the questions honestly - they\'re completely private',
      'Review your results when finished'
    ],
    tips: [
      'No account needed - the assessment is completely anonymous',
      'Your answers are never stored on our servers',
      'Be honest - the assessment is most helpful when you\'re truthful',
      'You can take the assessment multiple times',
      'Results are general guidance, not a medical diagnosis'
    ],
    relatedTopics: ['assessment-questions', 'assessment-results'],
    images: {
      desktop: '23-begin assessment.png'
    },
    searchKeywords: ['assessment', 'self-assessment', 'am I addicted', 'test', 'quiz', 'evaluate']
  },

  {
    id: 'assessment-questions',
    number: 24,
    title: 'Assessment Question Categories',
    category: 'assessment',
    description: 'Choose which addiction areas you want to explore in your self-assessment.',
    details: 'The assessment covers different types of addictions and compulsive behaviors. You can select one or multiple areas to assess. Each area has its own set of relevant questions.',
    steps: [
      'Review the list of assessment categories',
      'Click on each category you want to explore',
      'Selected categories are highlighted',
      'You can select as many or as few as you want',
      'Click "Continue" to begin answering questions',
      'You\'ll go through questions for each selected category'
    ],
    tips: [
      'Common categories include: Sex & Pornography, Alcohol, Drugs, etc.',
      'You can assess multiple areas - many people struggle with more than one',
      'Selecting more areas gives you a more complete picture',
      'You can always take the assessment again to explore other areas'
    ],
    relatedTopics: ['assessment-begin', 'assessment-answer-questions', 'assessment-results'],
    images: {
      desktop: '25-assessment choose areas to explore.png'
    },
    searchKeywords: ['assessment categories', 'addiction types', 'what to assess', 'choose areas']
  },

  {
    id: 'assessment-answer-questions',
    number: 26,
    title: 'Answering Assessment Questions',
    category: 'assessment',
    description: 'Respond to assessment questions to evaluate your relationship with addictive behaviors.',
    details: 'Each category you selected has a series of yes/no questions designed to help you reflect on patterns and behaviors. Answer honestly - this is for your benefit only.',
    steps: [
      'Read each question carefully',
      'Click "Yes" or "No" based on your honest experience',
      'There are no right or wrong answers',
      'Progress through all questions in each selected category',
      'Your answers contribute to your final assessment results'
    ],
    tips: [
      'Be honest - these answers are completely private and never shared',
      'Questions are based on standard addiction assessment criteria',
      'If you\'re unsure about a question, go with your gut feeling',
      'You can see how many questions remain as you progress'
    ],
    relatedTopics: ['assessment-questions', 'assessment-results'],
    images: {
      desktop: '26-sex and pornography questions - question 1.png'
    },
    searchKeywords: ['assessment questions', 'yes no', 'answer questions', 'taking assessment']
  },

  {
    id: 'assessment-results',
    number: 27,
    title: 'Understanding Your Assessment Results',
    category: 'assessment',
    description: 'Review your self-assessment results and understand what they mean.',
    details: 'After completing the questions, you\'ll see results for each area you assessed. The results provide guidance on whether you might benefit from recovery support and next steps.',
    steps: [
      'Review the results for each category you assessed',
      'Results indicate the likelihood of problematic behaviors',
      'Read the interpretation and recommendations',
      'Consider the suggested next steps',
      'If indicated, explore creating an account to begin recovery work'
    ],
    tips: [
      'Results are general guidance, not a professional diagnosis',
      'Higher scores suggest you may benefit from recovery support',
      'Everyone\'s journey is different - results are just a starting point',
      'You can retake the assessment anytime',
      'Consider talking to a healthcare professional or joining a recovery program'
    ],
    relatedTopics: ['assessment-begin', 'create-account', 'discovery'],
    images: {
      desktop: '27-assessment your results.png'
    },
    searchKeywords: ['results', 'assessment results', 'score', 'what do results mean', 'interpretation']
  },

  // ========================================
  // DISCOVERY (Topics 28-29)
  // ========================================
  {
    id: 'recovery-programs',
    number: 28,
    title: 'Learn About Recovery Programs',
    category: 'discovery',
    route: '/discover',
    description: 'Explore information about different 12-step and recovery programs.',
    details: 'The Discovery section provides information about various recovery programs, their approaches, and how they can help. This helps you understand your options and find the right program for your needs.',
    steps: [
      'Click "Discover" from the homepage or dashboard',
      'Browse the list of recovery programs',
      'Click on any program to learn more about it',
      'Read about the program\'s focus, approach, and resources',
      'Return to the list to explore other programs'
    ],
    tips: [
      'No account needed to browse recovery information',
      'Each program has its own approach and focus area',
      'Many people benefit from multiple programs',
      'Links to external resources and meetings are provided'
    ],
    relatedTopics: ['saa-resources', 'select-program'],
    images: {
      desktop: '28-recovery programs.png'
    },
    searchKeywords: ['discover', 'programs', 'recovery programs', 'learn about', '12 step', 'AA', 'NA', 'SAA']
  },

  {
    id: 'saa-resources',
    number: 29,
    title: 'SAA Tools & Resources',
    category: 'discovery',
    route: '/saa',
    description: 'Access SAA-specific tools, readings, and recovery resources.',
    details: 'The SAA Tools & Resources page is your gateway to Sex Addicts Anonymous materials, including the Green Book, videos, meeting information, and other recovery tools specific to sexual addiction recovery.',
    steps: [
      'From your dashboard, click "More SAA Resources"',
      'Or navigate to SAA from the Discovery section',
      'Browse available resources:',
      '  • Green Book readings',
      '  • Recovery videos and talks',
      '  • Meeting information',
      '  • SAA literature and materials',
      'Click on any resource to access it'
    ],
    tips: [
      'Resources are organized by type for easy finding',
      'New resources are added regularly',
      'All SAA materials are available whether you\'re logged in or not',
      'Bookmark this page if you reference SAA materials frequently'
    ],
    relatedTopics: ['recovery-programs', 'green-book-reading', 'dashboard'],
    images: {
      desktop: '29-saa tools and resources home page.png'
    },
    searchKeywords: ['SAA', 'resources', 'tools', 'green book', 'meetings', 'literature']
  }
];

export const helpCategories: HelpCategory[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    icon: '📖',
    description: 'Learn the basics: creating an account, verifying your email, and choosing your program',
    topicIds: ['landing-page', 'create-account', 'verify-account', 'verify-email-sample', 'select-program']
  },
  {
    id: 'dashboard',
    name: 'Dashboard & Navigation',
    icon: '🏠',
    description: 'Navigate your personal dashboard and understand your progress overview',
    topicIds: ['dashboard', 'step-status', 'hamburger-menu']
  },
  {
    id: 'steps',
    name: 'Working the Steps',
    icon: '📝',
    description: 'Learn how to work through recovery steps with readings, videos, and action items',
    topicIds: ['step-one-overview', 'green-book-reading', 'step-videos', 'step-completed']
  },
  {
    id: 'inventories',
    name: 'Step Four Inventories',
    icon: '📋',
    description: 'Complete your resentments, fears, and harms done inventories',
    topicIds: ['step-four-actions', 'resentments-inventory', 'inventory-detail']
  },
  {
    id: 'features',
    name: 'Tools & Features',
    icon: '🔧',
    description: 'Profile settings, sponsors, sharing progress, feedback, and backups',
    topicIds: ['field-help', 'profile', 'sponsors', 'share', 'send-snapshot', 'feedback', 'backup']
  },
  {
    id: 'assessment',
    name: 'Self-Assessment',
    icon: '📊',
    description: 'Take a confidential self-assessment to explore potential addiction concerns',
    topicIds: ['assessment-begin', 'assessment-questions', 'assessment-answer-questions', 'assessment-results']
  },
  {
    id: 'discovery',
    name: 'Discovery & Information',
    icon: '🔍',
    description: 'Learn about recovery programs, meetings, and resources',
    topicIds: ['recovery-programs', 'saa-resources']
  }
];

// Helper function to get a topic by ID
export const getTopicById = (id: string): HelpTopic | undefined => {
  return helpTopics.find(topic => topic.id === id);
};

// Helper function to get topics by category
export const getTopicsByCategory = (categoryId: string): HelpTopic[] => {
  const category = helpCategories.find(cat => cat.id === categoryId);
  if (!category) return [];
  
  return category.topicIds
    .map(id => getTopicById(id))
    .filter((topic): topic is HelpTopic => topic !== undefined)
    .sort((a, b) => a.number - b.number);
};

// Helper function to get next topic in sequence
export const getNextTopic = (currentId: string): HelpTopic | undefined => {
  const currentIndex = helpTopics.findIndex(topic => topic.id === currentId);
  if (currentIndex === -1 || currentIndex === helpTopics.length - 1) return undefined;
  return helpTopics[currentIndex + 1];
};

// Helper function to get previous topic in sequence
export const getPreviousTopic = (currentId: string): HelpTopic | undefined => {
  const currentIndex = helpTopics.findIndex(topic => topic.id === currentId);
  if (currentIndex <= 0) return undefined;
  return helpTopics[currentIndex - 1];
};

// Helper function to search topics
export const searchTopics = (query: string): HelpTopic[] => {
  const lowerQuery = query.toLowerCase();
  
  return helpTopics.filter(topic => {
    const searchableText = [
      topic.title,
      topic.description,
      topic.details,
      ...(topic.searchKeywords || []),
      ...(topic.steps || [])
    ].join(' ').toLowerCase();
    
    return searchableText.includes(lowerQuery);
  });
};

// Map routes to help topic IDs for context-sensitive help
export const routeToTopicMap: Record<string, string> = {
  '/': 'landing-page',
  '/register': 'create-account',
  '/verify': 'verify-account',
  '/onboarding': 'select-program',
  '/dashboard': 'dashboard',
  '/saa/12-steps': 'step-status',
  '/saa/12-steps/step-1': 'step-one-overview',
  '/saa/12-steps/step-4': 'step-four-actions',
  '/self-assess': 'assessment-begin',
  '/discover': 'recovery-programs',
  '/saa': 'saa-resources'
};

export const getTopicForRoute = (route: string): HelpTopic | undefined => {
  const topicId = routeToTopicMap[route];
  return topicId ? getTopicById(topicId) : undefined;
};

